import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import BackendService from '../services/backendService';

export type TenorGIF = {
  id: string;
  title: string;
  url: string;
  media_formats: {
    gif: {
      url: string;
      dims: [number, number];
    };
    tinygif: { url: string; dims: [number, number] };
  };
};

const fetchGifs = async ({
  search,
  limit = 20,
  cursor,
}: {
  search?: string;
  limit?: number;
  cursor?: string;
}) => {
  const res = await BackendService.instance.gif.search({ search, limit, cursor });
  return res;
};

export const useTenorGifs = (search?: string) => {
  const { data, isLoading } = useQuery(['tenor', search || 'trending'], () => {
    if (search) {
      return fetchGifs({ search });
    } else {
      return fetchGifs({
        search: undefined,
      });
    }
  });

  return {
    data,
    isLoading,
  };
};

export const useInifiniteTenorGifs = (search?: string) => {
  const actualSearch = search || undefined;
  const paginatedQuery = ({ pageParam = undefined }: { pageParam?: string }) =>
    fetchGifs({ search: actualSearch, cursor: pageParam });

  const {
    data,
    isFetching,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(['tenor', actualSearch], paginatedQuery, {
    getNextPageParam: lastPage => lastPage.cursor,
    staleTime: 1000 * 60 * 60,
  });

  const items = useMemo(() => data?.pages.flatMap(e => e.data), [data]);

  return {
    items,
    isFetchingMore: isFetchingNextPage,
    canFetchMore: hasNextPage,
    fetchMore: fetchNextPage,
    isFetching,
    error,
  };
};
